import {Alert} from 'platform/components';
import {Show, Space, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {CHECKOUT_PROFILE_VERSION_CONFLICT_CODES} from '../../../constants/CHECKOUT_PROFILE_VERSION_CONFLICT_CODES';
import type {CheckoutProfileIssue} from '../../../utils/getCheckoutProfileIssueMessage';
import {checkoutProfileOptions} from '../utils/checkoutProfileOptions';

export function CheckoutProfileFeedback(props: {
  issues: CheckoutProfileIssue[];
  isDirty: boolean;
  onSelect: (issue: CheckoutProfileIssue) => void;
  getNavigationTarget?: (issue: CheckoutProfileIssue) => CheckoutProfileIssue | undefined;
  showDocumentContext?: boolean;
  // Discards the draft and loads the stored profile after a version conflict.
  onReload?: () => void;
}) {
  const documents = checkoutProfileOptions.documents();
  const regimes = checkoutProfileOptions.regimes();
  const payments = checkoutProfileOptions.payments();

  return (
    <>
      <Show when={props.issues.length > 0}>
        <VStack spacing={2}>
          {props.issues.map((issue) => {
            const navigationTarget = isNotNil(props.getNavigationTarget)
              ? props.getNavigationTarget(issue)
              : issue;

            const context = [
              (props.showDocumentContext ?? true)
                ? regimes.find((regime) => regime.value === issue.saleRegime)?.label
                : undefined,
              (props.showDocumentContext ?? true) && isNotNil(issue.documentType)
                ? documents[issue.documentType]
                : undefined,
              payments.find((payment) => payment.value === issue.paymentMethod)?.label,
            ]
              .filter(isNotNil)
              .join(' / ');

            const title = isNotNilOrEmpty(context) ? `${context}: ${issue.message}` : issue.message;
            const reload = props.onReload;
            const hyperlinks = match({code: issue.code, target: navigationTarget})
              .with({code: Pattern.union(...CHECKOUT_PROFILE_VERSION_CONFLICT_CODES)}, () =>
                isNotNil(reload)
                  ? [{title: i18n.t('general.actions.refresh'), onClick: reload}]
                  : undefined
              )
              .with({target: {tab: Pattern.string}}, ({target}) => [
                {title: i18n.t('general.actions.view'), onClick: () => props.onSelect(target)},
              ])
              .otherwise(() => undefined);

            return (
              <Alert
                key={[
                  issue.code,
                  issue.field,
                  issue.tab,
                  issue.saleRegime,
                  issue.documentType,
                  issue.paymentMethod,
                ].join('|')}
                type="banner"
                variant={issue.severity}
                title={title}
                hyperlinks={hyperlinks}
              />
            );
          })}
        </VStack>
        <Space vertical={4} />
      </Show>
      <Show when={props.isDirty}>
        <Text size="small" color="secondary">
          {i18n.t('entity.checkout.validation.unsavedConfiguration')}
        </Text>
        <Space vertical={4} />
      </Show>
    </>
  );
}
